import React, { useState, useEffect, useMemo, useCallback } from "react";
import InitTrainingsLogic from "./Bookings.logic";
import { FaClock, FaUsers, FaCalendarTimes, FaMapMarkerAlt, FaUser } from "react-icons/fa";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "./Bookings.module.css";
import Loading from "../Loading/Loading.component";
import i18n, { t } from "i18next";
import { formatBookingDate as formatDate, formatLongDate, formatBookingTime as formatTime, bookingDayKey, calendarDayKey } from "../../helpers/dateFormat";

const BookingsComponent: React.FC = () => {
    const { selectedDate, client, bookings, location, locations, isLoading, setSelectedDate, onHome, onSettings, onBook, onCancel, onChangeLocation } = InitTrainingsLogic();
    const [filteredBookings, setFilteredBookings] = useState<any[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
    const [repeatDays, setRepeatDays] = useState<{ [key: string]: boolean }>({});
    const [activeTab, setActiveTab] = useState("Kalendar");
    const [showModal, setShowModal] = useState(false);
    const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);

    const dayMappingAlwaysDisplayed: Record<"Mon" | "Tue" | "Wed" | "Thu" | "Fri", string> = useMemo(() => ({
        Mon: "Pon",
        Tue: "Uto",
        Wed: "Sre",
        Thu: "Cet",
        Fri: "Pet",
    }), []);

    const dayMapping: Record<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun", string> = useMemo(() => ({
        Mon: "Pon",
        Tue: "Uto",
        Wed: "Sre",
        Thu: "Cet",
        Fri: "Pet",
        Sat: "Sub",
        Sun: "Ned"
    }), []);

    const { availableDatesSet, clientBookingsDatesSet } = useMemo(() => {
        const availableDatesSet = new Set(
            bookings
                .filter((b) => !b.cancelled && (b.clients?.length || 0) < b.maxNumberOfClients)
                .map((b) => bookingDayKey(b.date))
        );

        const clientBookingsDatesSet = new Set(
            client?.bookings
                ?.filter((b: any) => b.location === location)
                .map((b: any) => bookingDayKey(b.date))
        );

        return { availableDatesSet, clientBookingsDatesSet };
    }, [bookings, client?.bookings, location]);

    const tileClassName = useCallback(({ date }: any) => {
        const formattedDate = calendarDayKey(date);

        if (selectedDate && formattedDate === calendarDayKey(selectedDate)) {
            return null; // Selektovani datum
        }

        if (clientBookingsDatesSet.has(formattedDate)) {
            return styles.orangeHighlightedDate; // Klijentovi termini
        }

        if (availableDatesSet.has(formattedDate)) {
            return styles.highlightedDate; // Slobodni termini
        }

        return null;
    }, [availableDatesSet, clientBookingsDatesSet, selectedDate]);

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        // Profil se jos ucitava - bez guard-a klik na datum obara stranicu
        if (!client) {
            setFilteredBookings([]);
            return;
        }
        const selectedDateString = calendarDayKey(date);

        // Samo termini sa izabrane lokacije - kao i bojenje kalendara iznad
        const clientBookingsForDate = client.bookings?.filter((b: any) =>
            b.location === location &&
            bookingDayKey(b.date) === selectedDateString
        ).sort((a: any, b: any) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        ) || [];

        const generalBookings = bookings.filter((b) =>
            bookingDayKey(b.date) === selectedDateString &&
            !(client.bookings?.some((cb: any) => cb.id === b.id))
        );

        setFilteredBookings([...clientBookingsForDate, ...generalBookings]);
    };




    const handleBookingClick = (booking: any) => {
        setSelectedBooking(booking);
        // const selectedDayEng = new Date(booking.date).toLocaleDateString("en-US", {
        //     weekday: "short",
        // }) as keyof typeof dayMapping;
        // const selectedDay = dayMapping[selectedDayEng];
        // setRepeatDays((prev) => ({ ...prev, [selectedDay]: true }));
        setRepeatDays((prev) => ({ ...prev }));
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedBooking(null);
        setRepeatDays({});
    };

    const shouldShowSaturday = () => {
        if (selectedBooking) {
            const bookingDate = new Date(selectedBooking.date);
            const dayOfWeek = bookingDate.getUTCDay(); // 0 = Sunday, 6 = Saturday

            // Proveri da li je subota
            return dayOfWeek === 6;
        }
        return false;
    };


    const formatDateString = (date: Date) => formatLongDate(date);

    const handleDayToggle = (day: string) => {
        setRepeatDays((prevState) => ({
            ...prevState,
            [day]: !prevState[day],
        }));
    };


    useEffect(() => {
        if (selectedBooking) {
            // const selectedDayEng = new Date(selectedBooking.date).toLocaleDateString("en-US", {
            //     weekday: "short",
            // }) as keyof typeof dayMapping;
            // const selectedDay = dayMapping[selectedDayEng];
            /* setRepeatDays((prevState) => ({
                ...prevState,
                [selectedDay]: true,
            })); */
            setRepeatDays((prevState) => ({
                ...prevState,
            }));
        }
    }, [selectedBooking, dayMapping]);

    const handleCancelClick = (id: number) => {
        setSelectedBookingId(id);
        setShowModal(true);
    };

    const confirmCancel = () => {
        if (selectedBookingId !== null) {
            onCancel(selectedBookingId);
            setSelectedDate(null)
        }
        setShowModal(false);
    };

    const closeModalCancel = () => setShowModal(false);

    return (
        <div className={styles.container}>
            {/* <h1 className={styles.header}>Kalendar</h1> */}

            {/* Modal for Confirmation */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '5px',
                        width: '300px',
                        textAlign: 'center'
                    }}>
                        <p>{`${t("Da li ste sigurni da želite da otkažete ovaj trening?")}`}</p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginTop: '20px'
                        }}>
                            <button onClick={closeModalCancel} className="btn-secondary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>{`${t("Ne")}`}</button>
                            <button onClick={confirmCancel} className="btn-primary" style={{
                                flex: 1,
                                padding: '12px 20px',
                                borderRadius: '10px',
                                cursor: 'pointer'
                            }}>{`${t("Da")}`}</button>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.padding50}>
                <label htmlFor="location">{`${t("Lokacija")}`}</label>
                <select id="location" aria-label={t("Izaberi lokaciju")} onChange={onChangeLocation} name="location">
                    {locations.map((l: string, index: number) => (
                        <option key={index} value={l}>{`${t(l)}`}</option>
                    ))}
                </select>
            </div>
            {isLoading ? (
                <Loading />
            ) : (<>
                <div className={styles.calendarContainer}>
                    <Calendar
                        onClickDay={handleDateClick}
                        tileClassName={tileClassName}
                        view="month"
                        minDetail="month"
                        locale={i18n.language === "EN" ? "en" : "sr-Latn"}
                        minDate={new Date()}
                        className={styles.reactCalendar}
                    />
                </div>

                <br />

                {selectedDate && (
                    <div className={styles.selectedDateContainer}>
                        <h3>{formatDateString(selectedDate)}</h3>

                        {filteredBookings.length > 0 ? (
                            <div className={styles.bookingsList}>
                                {filteredBookings.map((b) => {
                                    const isClientBooking = client.bookings ? client.bookings.some((cb: any) => cb.id === b.id) : false;

                                    return (
                                        <div key={b.id} className={styles.bookingItem}>
                                            <div className={styles.bookingTime}>
                                                <FaClock className={styles.icon} />
                                                {formatTime(b.date)}
                                            </div>
                                            <div className={styles.bookingDetails}>
                                                <h4 className={styles.textAlignCenter}>{`${t(b.name)}`}</h4>
                                                <p className={styles.bookingLocation}>
                                                    <FaMapMarkerAlt className={styles.icon} />
                                                    {`${t(b.location)}`}
                                                </p>
                                                <p className={styles.bookingClientCount}>
                                                    <FaUsers className={styles.icon} />
                                                    {`${t("Zauzeto")}`}: {b.clients ? b.clients.length : 0}/{b.maxNumberOfClients}
                                                </p>
                                                {b.user && (
                                                    <p className={styles.bookingLocation}>
                                                        <FaUser className={styles.icon} />
                                                        {`${t("Trener")}`}: {b.user.firstName} {b.user.lastName}
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent parent onClick event if necessary
                                                    isClientBooking ? handleCancelClick(b.id) : handleBookingClick(b); // Toggle based on booking type
                                                }}
                                                className={styles.scheduleButton}
                                                style={isClientBooking ? { backgroundColor: 'var(--danger)', color: 'var(--white)' } : {}}
                                            >
                                                {isClientBooking ? `${t("Otkaži")}` : `${t("Zakaži")}`}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.noBookingsContainer}>
                                <FaCalendarTimes className={styles.noBookingsIcon} />
                                <h3 className={styles.noBookingsMessage}>{`${t("Nema aktivnosti za izabrani datum")}`}</h3>
                                <p className={styles.noBookingsSubtext}>{`${t("Za tvoj program nema treninga na ovaj dan")}`}</p>
                            </div>
                        )}
                    </div>
                )}

                {modalVisible && selectedBooking && (
                    <div className={styles.modalOverlay} onClick={closeModal}>
                        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            {/* Podebljane su samo labele, vrednosti normalne */}
                            <p style={{ fontWeight: 400 }}><b>{`${t("Lokacija")}:`}</b> {`${t(selectedBooking.location)}`}</p>
                            <p style={{ fontWeight: 400 }}><b>{`${t("Trening")}:`}</b> {`${t(selectedBooking.name)}`}</p>
                            <p style={{ fontWeight: 400 }}><b>{`${t("Datum")}:`}</b> {formatDate(selectedBooking.date)}</p>
                            <p style={{ fontWeight: 400 }}><b>{`${t("Vreme")}:`}</b> {formatTime(selectedBooking.date)}</p>
                            {selectedBooking.user && (
                                <p style={{ fontWeight: 400 }}><b>{`${t("Trener")}:`}</b> {`${selectedBooking.user.firstName} ${selectedBooking.user.lastName}`}</p>
                            )}

                            {/* <h4>Trening se ponavlja danima:</h4>
                            <div className={styles.repeatDaysContainer}>
                                {Object.keys(dayMappingAlwaysDisplayed).map((dayEng) => {
                                    const daySrpski = dayMapping[dayEng as keyof typeof dayMapping];
                                    return (
                                        <div key={daySrpski} className={styles.repeatDay}>
                                            <input
                                                type="checkbox"
                                                checked={repeatDays[daySrpski] || false}
                                                onChange={() => handleDayToggle(daySrpski)}
                                            />
                                            {daySrpski}
                                        </div>
                                    );
                                })}
                                {shouldShowSaturday() && (
                                    <div key="Sub" className={styles.repeatDay}>
                                        <input
                                            type="checkbox"
                                            checked={repeatDays["Sub"] || false}
                                            onChange={() => handleDayToggle("Sub")}
                                        />
                                        <label>Sub</label>
                                    </div>
                                )}
                            </div> */}

                            <div className={styles.modalButtons}>
                                <button className={styles.saveButton} onClick={() => { onBook({ id: selectedBooking.id, repeatDays }); setSelectedDate(null); closeModal() }}>
                                    {`${t("Sačuvaj")}`}
                                </button>
                                <button className={styles.cancelButton} onClick={closeModal}>
                                    {`${t("Odustani")}`}
                                </button>
                            </div>
                        </div>
                    </div>
                )
                }

                {/* Bottom Navigation Tabs */}
                <div style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    backgroundColor: 'var(--white)',
                    borderTop: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-around',
                    padding: '10px 8px',
                    gap: '8px',
                    zIndex: 100
                }}>
                    <button
                        style={{
                            flex: 1,
                            maxWidth: 160,
                            padding: '10px 12px',
                            background: activeTab === "Početna" ? 'var(--secondary)' : 'transparent',
                            color: 'var(--text)',
                            fontWeight: activeTab === "Početna" ? 600 : 400,
                            borderRadius: 10,
                        }}
                        onClick={() => { setActiveTab("Početna"); onHome() }}
                    >
                        {`${t("Početna")}`}
                    </button>
                    <button
                        style={{
                            flex: 1,
                            maxWidth: 160,
                            padding: '10px 12px',
                            background: activeTab === "Kalendar" ? 'var(--secondary)' : 'transparent',
                            color: 'var(--text)',
                            fontWeight: activeTab === "Kalendar" ? 600 : 400,
                            borderRadius: 10,
                        }}
                        onClick={() => {
                            setActiveTab("Kalendar");
                        }}
                    >
                        {`${t("Kalendar")}`}
                    </button>
                    <button
                        style={{
                            flex: 1,
                            maxWidth: 160,
                            padding: '10px 12px',
                            background: activeTab === "Podešavanja" ? 'var(--secondary)' : 'transparent',
                            color: 'var(--text)',
                            fontWeight: activeTab === "Podešavanja" ? 600 : 400,
                            borderRadius: 10,
                        }}
                        onClick={() => {
                            setActiveTab("Podešavanja");
                            onSettings();
                        }}
                    >
                        {`${t("Podešavanja")}`}
                    </button>
                </div>
            </>
            )
            }
        </div >
    );
};

export default BookingsComponent;
